import {
  Button,
  Checkbox,
  Col,
  Popconfirm,
  Row,
  Switch,
  Table,
  TableProps,
  Tag,
  Typography,
} from "antd";
import { useState, useEffect } from "react";

import AdminLayout from "../../../component/AdminLayout";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { SorterResult } from "antd/es/table/interface";
import SearchBar from "../../../component/Search";
import { enqueueSnackbar } from "notistack";
import { customSortDirections } from "../../../Utils/utils";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useAppRole, useUserRole } from "../../../API/RoleContext";
import VersionManagement from "../components/VersionManagement";
import AddNewSoftware from "../components/AddNewSoftware";
import EditSoftware from "../components/EditSoftware";
import {
  DeleteSoftware,
  GetSoftwareList,
  axiosErrorMessages,
} from "../../../API/api";
import axios, { AxiosError } from "axios";

interface softwareItem {
  softwareId: number;
  softwareName: string;
  companyName: string;
  versionCount: number;
  paidOrFree: string;
  status: string;
}
const Configuration = () => {
  const appRole = useAppRole();

  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [openVersionManagementPopup, setOpenVersionManagementPopup] =
    useState<boolean>(false);
  const [openAddNewSoftwarePopup, setOpenAddNewSoftwarePopup] =
    useState<boolean>(false);
  const [softwareId, setSoftwareId] = useState<number>(0);
  const [editData, setEditData] = useState({});
  const [openEditPopup, setOpenEditPopup] = useState<boolean>(false);
  const [softwareList, setSoftwareList] = useState([]);
  const [filteredData, setFilteredData] = useState<softwareItem[]>([]);
  const [pagesize, setPageSize] = useState<number>(50);

  const [sortedInfo, setSortedInfo] = useState<SorterResult<softwareItem>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchText, setSearchText] = useState("");
  const [isOs, setIsOs] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (searchText) filterData(searchText);
  }, [softwareList]);
  const handleDelete = (data: any) => {
    setIsLoading(true);
    DeleteSoftware(data)
      .then((response: any) => {
        enqueueSnackbar("Deleted Successfully", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });

        setIsLoading(false);
        getSoftwareList();
      })
      .catch((error: any) => {
        if (error.response.data.errors.Software[0])
          enqueueSnackbar(error.response.data.errors.Software[0], {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        else
          enqueueSnackbar("Error in Deleting", {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getSoftwareList();
  }, [isOs]);

  const getSoftwareList = async () => {
    try {
      setIsLoading(true);
      const response = await GetSoftwareList(isOs);
      const newData = response.data.map((software: any, index: number) => ({
        ...software,
        key: software.softwareId,
      }));
      setSoftwareList(newData);
      setIsLoading(false);
    } catch (error: any) {
      let errorMsg;
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        errorMsg = axiosErrorMessages(axiosError);
      } else {
        errorMsg = error.message;
      }
      enqueueSnackbar(errorMsg, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });

      setIsLoading(false);
    }
  };
  const edit = (record: Partial<softwareItem> & { key: React.Key }) => {
    setEditData(record);
    setOpenEditPopup(true);
  };
  const columns = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      width: "10%",
      render: (text: any, record: any, index: number) =>
        (currentPage - 1) * pagesize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "softwareName",
      key: "softwareName",
      width: "40%",
      sorter: (a: any, b: any) => a.softwareName.localeCompare(b.softwareName),
      sortOrder:
        sortedInfo.columnKey === "softwareName" ? sortedInfo.order : null,
      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      width: "40%",
      sorter: (a: any, b: any) => a.companyName.localeCompare(b.companyName),
      sortOrder:
        sortedInfo.columnKey === "companyName" ? sortedInfo.order : null,
      render: (text: string, record: any) => <div>{text}</div>,
    },
    {
      title: "Version",
      dataIndex: "versionCount",
      key: "versionCount",
      width: "40%",
      render: (text: string, record: any) => (
        <Typography.Link onClick={() => versionManagementOpen(record)}>
          {text}
        </Typography.Link>
      ),
    },
    ...(appRole
      ? [
          {
            title: "Action",
            dataIndex: "action",
            width: "20%",

            render: (_: any, record: any) => {
              return (
                <>
                  {" "}
                  <Typography.Link onClick={() => edit(record)}>
                    <EditOutlined />
                  </Typography.Link>
                  <Popconfirm
                    title="Do you want to delete the software?"
                    onConfirm={() => handleDelete(record.key)}
                  >
                    <Typography.Link className="delete-btn">
                      <DeleteOutlined />
                    </Typography.Link>
                  </Popconfirm>
                </>
              );
            },
          },
        ]
      : []),
  ];

  const versionManagementOpen = (rowData: any) => {
    setSoftwareId(rowData.softwareId);
    setOpenVersionManagementPopup(true);
  };

  const handleSearch = (value: string) => {
    setCurrentPage(1);
    setSearchText(value);
    filterData(value);
  };
  const filterData = (searchText: string) => {
    if (!searchText) {
      setFilteredData([]);
    } else {
      const filtered = softwareList.filter((item: any) =>
        item.softwareName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    }
  };
  const handleChange: TableProps<softwareItem>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setSortedInfo(sorter as SorterResult<softwareItem>);
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize ?? 50);
  };
  const handleAddNewSoftware = () => {
    setIsAdd(true);
    setOpenAddNewSoftwarePopup(true);
  };
  const handleCloseVersion = () => {
    setOpenVersionManagementPopup(false);
    getSoftwareList();
  };
  const handleClose = () => {
    setOpenAddNewSoftwarePopup(false);
    getSoftwareList();
  };
  const isCheckedIsOs = (e: any) => {
    setIsOs(e.target.checked);
  };
  return (
    <>
      <AdminLayout>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <div>
          <div className="employee-table-header ">
            <Row
              gutter={16}
              style={{ alignItems: "center", margin: "12px 16px 0 16px" }}
            >
              <Col className="gutter-row" lg={12} sm={24}>
                <h3 className="bottom-margin-mob" style={{ float: "left" }}>
                  Software Settings
                </h3>
              </Col>
              <Col
                className="gutter-row pm-search-flex"
                lg={{ span: 10, offset: 2 }}
                sm={24}
                xs={24}
                style={{ display: "flex" }}
              >
                <SearchBar
                  width={"100%"}
                  onSearch={handleSearch}
                  placeholder={"Search Software"}
                />
                {appRole && (
                  <Button
                    onClick={handleAddNewSoftware}
                    type="primary"
                    style={{ marginLeft: 10 }}
                    size="small"
                  >
                    Add New
                  </Button>
                )}
              </Col>
            </Row>
          </div>
          <div>
            <Row gutter={16}>
              <Col className="gutter-row" lg={12} sm={24}>
                <h3
                  className="bottom-margin-mob"
                  style={{
                    float: "left",
                    marginBottom: "25px",
                    marginLeft: "28px",
                  }}
                >
                  <Checkbox checked={isOs} onChange={isCheckedIsOs}>
                    OS
                  </Checkbox>
                </h3>
              </Col>
            </Row>
          </div>

          <Table
            sortDirections={customSortDirections}
            scroll={{ x: 1000, y: 450 }}
            className="status-list-admin"
            columns={columns}
            size="small"
            onChange={handleChange}
            pagination={{
              current: currentPage,
              showSizeChanger: true,
              defaultPageSize: 50,
            }}
            rowClassName="editable-row"
            dataSource={searchText ? filteredData : softwareList}
          />
          {openVersionManagementPopup && (
            <VersionManagement
              softwareId={softwareId}
              handleCloseVersion={handleCloseVersion}
            />
          )}
          {openAddNewSoftwarePopup && (
            <AddNewSoftware handleClose={handleClose} />
          )}
          {openEditPopup && (
            <EditSoftware
              isOs={isOs}
              editData={editData}
              handleClose={(isReload: boolean) => {
                if (isReload) getSoftwareList();
                setOpenEditPopup(false);
              }}
            />
          )}
        </div>
      </AdminLayout>
    </>
  );
};
export default Configuration;
